"use client";

import Link from "next/link";
import { Home, Search } from "lucide-react";
import { SiteHeader } from "@/components/layout/site-header";
import { Footer } from "@/components/layout/footer";
import { SkyPageHero } from "@/components/sections/sky/sky-page-hero";
import { useDemoPopup } from "@/contexts/demo-popup-context";

function RunningCat() {
  return (
    <svg
      viewBox="0 0 280 140"
      className="mx-auto h-auto w-60 sm:w-72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Motion lines */}
      <path
        d="M30 70 L10 70"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        className="text-muted-foreground/40"
      />
      <path
        d="M35 55 L15 50"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-muted-foreground/30"
      />
      <path
        d="M35 85 L15 90"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-muted-foreground/30"
      />
      <path
        d="M45 45 L30 35"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="text-muted-foreground/20"
      />
      <path
        d="M45 95 L30 105"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="text-muted-foreground/20"
      />

      {/* Tail - curved and flowing back */}
      <path
        d="M60 65 Q30 50 25 30 Q22 20 30 15"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        className="text-gray-400 dark:text-gray-500"
      />

      {/* Back leg (stretched back) */}
      <path
        d="M75 85 L50 110 L45 125"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-400 dark:text-gray-500"
      />

      {/* Body - elongated for running pose */}
      <ellipse
        cx="120"
        cy="70"
        rx="55"
        ry="28"
        className="fill-gray-300 dark:fill-gray-400"
      />

      {/* Front leg (stretched forward) */}
      <path
        d="M165 80 L195 95 L210 120"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-400 dark:text-gray-500"
      />

      {/* Other front leg (mid-stride) */}
      <path
        d="M150 85 L160 110 L155 125"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-300 dark:text-gray-400"
      />

      {/* Head */}
      <circle
        cx="190"
        cy="55"
        r="28"
        className="fill-gray-300 dark:fill-gray-400"
      />

      {/* Ears */}
      <path
        d="M172 35 L165 10 L180 25"
        className="fill-gray-400 dark:fill-gray-500"
      />
      <path
        d="M200 30 L210 5 L215 25"
        className="fill-gray-400 dark:fill-gray-500"
      />
      {/* Inner ears */}
      <path d="M170 32 L167 15 L177 26" className="fill-pink-200" />
      <path d="M203 28 L210 12 L213 26" className="fill-pink-200" />

      {/* Face - looking back nervously */}
      {/* Eyes - wide and startled */}
      <circle cx="182" cy="50" r="6" className="fill-white" />
      <circle cx="200" cy="48" r="6" className="fill-white" />
      <circle cx="184" cy="51" r="3" className="fill-gray-800" />
      <circle cx="202" cy="49" r="3" className="fill-gray-800" />
      {/* Eye shine */}
      <circle cx="183" cy="49" r="1" className="fill-white" />
      <circle cx="201" cy="47" r="1" className="fill-white" />

      {/* Nose */}
      <ellipse cx="210" cy="58" rx="4" ry="3" className="fill-pink-300" />

      {/* Whiskers */}
      <path
        d="M215 55 L235 50"
        stroke="currentColor"
        strokeWidth="1"
        className="text-gray-500"
      />
      <path
        d="M215 58 L238 58"
        stroke="currentColor"
        strokeWidth="1"
        className="text-gray-500"
      />
      <path
        d="M215 61 L235 66"
        stroke="currentColor"
        strokeWidth="1"
        className="text-gray-500"
      />

      {/* Sweat drop */}
      <path
        d="M225 35 Q230 25 225 20 Q220 25 225 35"
        className="fill-sky-300"
      />

      {/* Dust cloud */}
      <circle cx="55" cy="120" r="8" className="fill-amber-100/60 dark:fill-amber-200/30" />
      <circle cx="40" cy="115" r="6" className="fill-amber-100/40 dark:fill-amber-200/20" />
      <circle cx="65" cy="125" r="5" className="fill-amber-100/40 dark:fill-amber-200/20" />
      <circle cx="30" cy="120" r="4" className="fill-amber-100/30 dark:fill-amber-200/10" />
    </svg>
  );
}

export default function NotFound() {
  const { openPopup } = useDemoPopup();

  return (
    <>
      <SiteHeader overSky />
      <main className="overflow-x-clip pb-16 md:pb-24">
        <SkyPageHero
          eyebrow="Błąd 404"
          title="Ta strona uciekła!"
          lead="Wygląda na to, że ta strona uciekła szybciej niż kot od weterynarza. Pewnie gdzieś się schowała pod kanapą..."
          after={
            <div className="mx-auto w-fit rounded-[28px] bg-white px-6 py-5 shadow-2xl shadow-[#021A9E]/30 sm:px-10">
              <RunningCat />
            </div>
          }
        >
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href="/"
              className="inline-flex h-12 w-full items-center justify-center rounded-md bg-white px-8 text-base font-semibold text-primary shadow-xl shadow-[#021A9E]/30 transition hover:bg-white/90 sm:w-auto"
            >
              <Home className="mr-2 h-4 w-4" />
              Strona główna
            </Link>
            <Link
              href="/funkcjonalnosci"
              className="inline-flex h-12 w-full items-center justify-center rounded-md bg-white/10 px-8 text-base font-semibold text-white ring-1 ring-white/70 transition hover:bg-white/20 sm:w-auto"
            >
              <Search className="mr-2 h-4 w-4" />
              Zobacz funkcje
            </Link>
          </div>

          <p className="mt-8 text-sm text-white/90">
            Jeśli uważasz, że to błąd,{" "}
            <button
              onClick={() => openPopup("not_found_error_report")}
              className="font-semibold text-white underline underline-offset-2 hover:no-underline"
            >
              daj nam znać
            </button>
            .
          </p>
        </SkyPageHero>
      </main>
      <Footer />
    </>
  );
}
